import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  // Keep eager images sharp when the shopper changes from two columns to one.
  #imageSizesObserver = new MutationObserver(() => this.#updateImageSizes());

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#imageSizesObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['product-grid-view'],
    });
    this.#updateImageSizes();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#imageSizesObserver.disconnect();
  }

  #updateImageSizes() {
    const grid = this.querySelector('.product-grid');
    if (!grid) return;
    const singleColumn = !mediaQueryLarge.matches && grid.getAttribute('product-grid-view') === 'mobile-single';
    for (const gallery of grid.querySelectorAll('[data-card-image-sizes]')) {
      const defaultSizes = gallery.getAttribute('data-card-image-sizes');
      if (!defaultSizes) continue;
      const sizes = singleColumn ? defaultSizes.replace(/, [^,]+$/, ', 100vw') : defaultSizes;
      for (const image of gallery.querySelectorAll('img.product-media__image')) {
        const nextSizes = image.loading === 'lazy' ? `auto, ${sizes}` : sizes;
        if (image.getAttribute('sizes') !== nextSizes) image.setAttribute('sizes', nextSizes);
      }
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
